/**
 * La couche HTTP — **le seul endroit du client qui connaisse `fetch`** (hors manifeste audio).
 *
 * Elle ne sait rien du jeu : elle parle le format d'erreur unique de SPEC-API §5.0
 *
 * ```json
 * { "error": { "code": "snake_case", "message": "phrase en français", "details": {} } }
 * ```
 *
 * et le rend sous forme d'`ErreurApi` typée. **Le `code` et les `details` sont la vérité, jamais
 * le message** : le serveur le dit lui-même dans `routes/actions.ts` — `InvalidAction` fait
 * `super(code)`, donc le message d'un refus moteur *est* un identifiant technique. C'est
 * `details.code` qu'on teste, et `client/src/game/erreurs.ts` possède déjà la formulation joueur
 * de chacun des codes.
 *
 * Trois choix qui tiennent le reste :
 *
 * 1. **Même origine, aucune configuration.** Le client est servi par le même Fastify que l'API
 *    (SPEC-API §4.1 : « aucune config CORS n'est nécessaire ni autorisée en V1 »), donc les
 *    chemins sont relatifs et le cookie de session part tout seul avec `credentials: "same-origin"`.
 * 2. **Un échec réseau n'est pas une erreur d'API.** `ErreurReseau` est une classe à part :
 *    c'est elle, et elle seule, qui autorise une **reprise** — rejouer un `409` ou un `422`
 *    n'aurait aucun sens.
 * 3. **La perte de session se signale une fois, en haut.** Un `401 unauthenticated` peut tomber
 *    sur n'importe quel appel (cookie expiré, base réinitialisée) ; plutôt que de le traiter dans
 *    quinze appelants, on prévient l'abonné unique — le store de session, qui ramène le joueur à
 *    l'écran de connexion.
 */

/** Le corps d'erreur normatif de §5.0, tel qu'il arrive sur le fil. */
interface CorpsErreurApi {
  error?: {
    code?: unknown;
    message?: unknown;
    details?: unknown;
  };
}

/**
 * Une réponse d'erreur de l'API, décodée. `status` + `code` + `details` : de quoi décider,
 * sans jamais lire le message (qui n'est là que pour être affiché tel quel en dernier recours).
 */
export class ErreurApi extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "ErreurApi";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** `details.reason` de §5.5 — `engine_rejected`, `atelier_shape`, `unknown_video`… */
  get raison(): string | null {
    return typeof this.details.reason === "string" ? this.details.reason : null;
  }
}

/**
 * Le serveur n'a pas répondu (coupure, DNS, TLS, onglet hors ligne). **Rien n'est décidé** :
 * la requête a pu partir, arriver, être résolue et perdre sa réponse — c'est exactement le cas
 * que l'idempotence de §7.2 rend sûr, à condition de **rejouer le même `action_id`**.
 */
export class ErreurReseau extends Error {
  constructor(readonly cause?: unknown) {
    super("Le serveur ne répond pas. Ta partie est en sécurité — on réessaie.");
    this.name = "ErreurReseau";
  }
}

/** Le préfixe de toutes les routes. Relatif : même origine que la page (§4.1). */
const BASE = "/api";

type Methode = "GET" | "POST" | "PATCH" | "DELETE";

export interface OptionsAppel {
  methode?: Methode;
  /** Sérialisé en JSON. Absent ⇒ pas de corps, pas d'en-tête `Content-Type`. */
  corps?: unknown;
  signal?: AbortSignal;
  /**
   * Laisse passer le `401` au lieu de le signaler comme une perte de session. Réservé au
   * **sondage** de session au démarrage : ne pas être connecté n'est pas un incident.
   */
  silencieuxSi401?: boolean;
}

/** Abonnés prévenus quand un `401 unauthenticated` tombe hors sondage. */
const abonnesSessionPerdue = new Set<() => void>();

/**
 * S'abonner à la perte de session. Rend la fonction de désabonnement (`useEffect` friendly).
 * Un seul abonné en pratique : `stores/sessionStore.ts`.
 */
export function surSessionPerdue(rappel: () => void): () => void {
  abonnesSessionPerdue.add(rappel);
  return () => abonnesSessionPerdue.delete(rappel);
}

function signalerSessionPerdue(): void {
  for (const rappel of abonnesSessionPerdue) rappel();
}

/** Décode le corps d'erreur §5.0. Tolérant : un 502 d'un reverse-proxy n'est pas du JSON. */
async function lireErreur(reponse: Response): Promise<ErreurApi> {
  let corps: CorpsErreurApi | null = null;
  try {
    corps = (await reponse.json()) as CorpsErreurApi;
  } catch {
    corps = null;
  }
  const erreur = corps?.error;
  const code = typeof erreur?.code === "string" ? erreur.code : `http_${reponse.status}`;
  const message =
    typeof erreur?.message === "string" && erreur.message.length > 0
      ? erreur.message
      : "Le serveur a refusé la requête.";
  const details =
    erreur?.details && typeof erreur.details === "object"
      ? (erreur.details as Record<string, unknown>)
      : {};
  return new ErreurApi(reponse.status, code, message, details);
}

/**
 * Un appel à `/api/*`. Rend le corps JSON typé, ou lève :
 * `ErreurReseau` (rien n'est décidé) ou `ErreurApi` (le serveur a tranché).
 *
 * ⚠️ Le type `T` est une **promesse de forme**, pas une garantie : on ne revalide pas les
 * réponses du serveur avec Zod. Le serveur est autoritatif et sa forme est spécifiée (§5) ;
 * revalider ici, ce serait dupliquer la spec côté client, et la faire diverger.
 */
export async function appelApi<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  const { methode = "GET", corps, signal, silencieuxSi401 = false } = options;

  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}${chemin}`, {
      method: methode,
      // Le cookie de session est `HttpOnly` + `SameSite=Lax` (§4.1) : il part de lui-même,
      // mais seulement si on le demande explicitement.
      credentials: "same-origin",
      headers: corps === undefined ? { accept: "application/json" } : { accept: "application/json", "content-type": "application/json" },
      body: corps === undefined ? undefined : JSON.stringify(corps),
      signal,
    });
  } catch (e) {
    // `AbortError` n'est pas une panne réseau : c'est nous qui avons annulé.
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ErreurReseau(e);
  }

  if (reponse.ok) {
    if (reponse.status === 204) return undefined as T;
    return (await reponse.json()) as T;
  }

  const erreur = await lireErreur(reponse);
  if (erreur.status === 401 && !silencieuxSi401) signalerSessionPerdue();
  throw erreur;
}

/** Une pause, pour espacer les reprises. Aucun `setInterval` : une seule attente, bornée. */
export function attendre(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Un UUID v4 — l'`action_id` de §5.5 est validé par `z.string().uuid()` côté serveur.
 *
 * `crypto.randomUUID` n'existe que dans un **contexte sécurisé** (https, ou localhost) : sur
 * une prévisualisation servie en http depuis une IP de LAN, il est absent. On retombe alors sur
 * `getRandomValues`, qui est disponible partout, avec les bits de version et de variante posés
 * à la main — un UUID mal formé serait rejeté en `422` et le joueur ne saurait pas pourquoi.
 */
export function uuid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const o = crypto.getRandomValues(new Uint8Array(16));
  o[6] = (o[6]! & 0x0f) | 0x40; // version 4
  o[8] = (o[8]! & 0x3f) | 0x80; // variante RFC 4122
  const hex = Array.from(o, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * ⟦SPEC-API §5.10 · A-81⟧ Un `GET` **conditionnel** : `If-None-Match` porte l'ETag de la
 * dernière lecture, et un `304` dit « rien n'a changé » sans renvoyer le corps. C'est le
 * contrat de `GET /api/content` (`ETag = content_version`), et c'est le seul appel du client
 * qui en ait l'usage — d'où une fonction à part plutôt qu'une option de plus sur `appelApi`.
 */
export async function appelApiConditionnel<T>(
  chemin: string,
  etag: string | null,
): Promise<{ statut: 304 } | { statut: 200; corps: T; etag: string | null }> {
  let reponse: Response;
  try {
    reponse = await fetch(`${BASE}${chemin}`, {
      method: "GET",
      credentials: "same-origin",
      headers: etag ? { accept: "application/json", "if-none-match": etag } : { accept: "application/json" },
    });
  } catch (e) {
    throw new ErreurReseau(e);
  }
  if (reponse.status === 304) return { statut: 304 };
  if (reponse.ok) {
    return { statut: 200, corps: (await reponse.json()) as T, etag: reponse.headers.get("etag") };
  }
  const erreur = await lireErreur(reponse);
  if (erreur.status === 401) signalerSessionPerdue();
  throw erreur;
}
